/* eslint-disable */
import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/material';
import type { StabilizationBug, StabilizationStats } from '../../types/release';

interface StabilizationBugsProps {
  bugs: StabilizationBug[];
  stats?: StabilizationStats;
}

export const StabilizationBugs: React.FC<StabilizationBugsProps> = ({ bugs, stats }) => {
  if (bugs.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Stabilization Bugs
        </Typography>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No stabilization bugs found for this release.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Try adjusting your filters or search query.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Stabilization Bugs ({bugs.length})
      </Typography>
      
      {stats && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    By Product X Tech Scope
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {Object.entries(stats.by_product_scope).map(([scope, count]) => (
                      <Chip
                        key={scope}
                        label={`${scope}: ${count}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {stats.timeline && stats.timeline.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Timeline
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {stats.timeline.map((entry) => (
                        <Box key={entry.date} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">{entry.date}</Typography>
                          <Typography variant="caption" fontWeight="bold">{entry.count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
      
      <Box>
        {bugs.map((bug) => (
          <Card key={bug.id} sx={{ mb: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="a" href={bug.link} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 'bold' }}>
                  {bug.id}
                </Typography>
                <Chip label={bug.status} size="small" />
                {bug.product_scope && (
                  <Chip label={bug.product_scope} size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="body2">{bug.title}</Typography>
              {bug.linked_epic && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Linked to Epic: {bug.linked_epic}
                </Typography>
              )}
              {bug.related_story_task && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Related to: {bug.related_story_task}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Created: {bug.created_date}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
